import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, Info, Calendar, DollarSign, FileText, Tag, BookOpen, Zap, CheckCircle } from 'lucide-react';

const API = 'https://api.naijapredict.net';

const CATEGORIES = ['Crypto','Politics','Sports','Economy','Entertainment','Technology','Weather','Other'];

export default function CreateMarket() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError]     = useState('');
  const [form, setForm] = useState({
    question: '',
    category: 'Crypto',
    resolutionSource: '',
    expiresAt: '',
    minVolume: '50',
  });

  function set(k, v) { setForm(f=>({...f,[k]:v})); }

  async function submit(e) {
    e.preventDefault();
    if (!form.question || !form.resolutionSource || !form.expiresAt)
      return setError('Please fill all required fields');
    setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('creator_token');
      const res = await fetch(API + '/api/creator/markets', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:'Bearer '+token },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSuccess(data);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }

  if (success) return (
    <div style={{ maxWidth:560, margin:'0 auto', textAlign:'center', paddingTop:48 }}>
      <div style={{ width:72, height:72, borderRadius:20, background:'linear-gradient(135deg,#00c566,#00a854)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
        <CheckCircle size={36} color="#000" strokeWidth={2}/>
      </div>
      <h2 style={{ fontSize:28, fontWeight:800, fontFamily:"'Syne',sans-serif", marginBottom:8 }}>Market Created!</h2>
      <p style={{ color:'#4a5a63', marginBottom:24, lineHeight:1.7 }}>
        Your market is now live on the blockchain. Platform has seeded it with $10 USDC. Start sharing to drive volume and earn royalties!
      </p>
      <div className="card" style={{ marginBottom:24, textAlign:'left' }}>
        <div style={{ fontSize:14, fontWeight:700, marginBottom:12 }}>{form.question}</div>
        {[
          ['Market ID', `#${success.marketId}`],
          ['TX Hash', success.txHash?.slice(0,20)+'...'],
          ['Seed Amount', '$10 USDC (platform funded)'],
          ['Your Royalty', '0.5% of all trades'],
        ].map(([k,v])=>(
          <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,.04)', fontSize:13 }}>
            <span style={{ color:'#4a5a63' }}>{k}</span>
            <span style={{ fontWeight:600 }}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{ display:'flex', gap:12 }}>
        <button className="btn btn-primary" onClick={()=>navigate('/markets')} style={{ flex:1 }}>View My Markets</button>
        <button className="btn btn-secondary" onClick={()=>{ setSuccess(null); setForm({question:'',category:'Crypto',resolutionSource:'',expiresAt:'',minVolume:'50'}); }} style={{ flex:1 }}>Create Another</button>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth:680, margin:'0 auto' }}>
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:28, fontWeight:800, fontFamily:"'Syne',sans-serif", marginBottom:4 }}>Create Market</h1>
        <p style={{ fontSize:14, color:'#4a5a63' }}>Platform seeds your market with $10 USDC — you risk nothing and earn 0.5% royalties</p>
      </div>

      {/* Info Banner */}
      <div style={{ background:'rgba(0,197,102,.04)', border:'1px solid rgba(0,197,102,.12)', borderRadius:14, padding:'14px 18px', marginBottom:24, display:'flex', gap:12, alignItems:'flex-start' }}>
        <Zap size={18} color="#00c566" strokeWidth={2} style={{ flexShrink:0, marginTop:2 }}/>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:'#00c566', marginBottom:4 }}>Zero Capital Required</div>
          <div style={{ fontSize:12, color:'#4a5a63', lineHeight:1.7 }}>
            NaijaPredict seeds every creator market with $10 USDC. You earn 0.5% royalties on all trading volume with absolutely zero financial risk.
          </div>
        </div>
      </div>

      <form onSubmit={submit}>
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontSize:14, fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
            <FileText size={16} color="#00c566"/> Market Question
          </div>
          <div style={{ marginBottom:16 }}>
            <label>QUESTION *</label>
            <textarea value={form.question} onChange={e=>set('question',e.target.value)} rows={3}
              placeholder="Will Bitcoin exceed $150,000 before December 31, 2026?" style={{ resize:'vertical' }}/>
            <div style={{ fontSize:11, color:'#4a5a63', marginTop:4 }}>
              Write a clear, binary YES/NO question with a specific end date
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16 }}>
            <div>
              <label><Tag size={10} style={{ marginRight:4 }}/>CATEGORY *</label>
              <select value={form.category} onChange={e=>set('category',e.target.value)}>
                {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label><Calendar size={10} style={{ marginRight:4 }}/>EXPIRY DATE *</label>
              <input type="datetime-local" value={form.expiresAt} onChange={e=>set('expiresAt',e.target.value)} min={new Date().toISOString().slice(0,16)}/>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontSize:14, fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
            <BookOpen size={16} color="#00c566"/> Resolution Details
          </div>
          <div style={{ marginBottom:16 }}>
            <label>RESOLUTION SOURCE *</label>
            <input value={form.resolutionSource} onChange={e=>set('resolutionSource',e.target.value)}
              placeholder="e.g. CoinMarketCap, BBC News, Official Government Statement"/>
            <div style={{ fontSize:11, color:'#4a5a63', marginTop:4 }}>Where will the outcome be officially confirmed?</div>
          </div>
          <div>
            <label><DollarSign size={10} style={{ marginRight:4 }}/>MINIMUM VOLUME ($USDC)</label>
            <input type="number" value={form.minVolume} onChange={e=>set('minVolume',e.target.value)} min="10" max="10000"/>
            <div style={{ fontSize:11, color:'#4a5a63', marginTop:4 }}>If volume stays below this, market auto-cancels and all funds are refunded</div>
          </div>
        </div>

        <div className="card" style={{ marginBottom:24, background:'rgba(255,255,255,.02)' }}>
          <div style={{ fontSize:13, fontWeight:700, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
            <Info size={14} color="#4a5a63"/> Market Summary
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {[
              ['Platform Seed',    '$10 USDC (auto-funded by platform)'],
              ['Your Investment',  '$0.00 — zero risk'],
              ['Your Royalty',     '0.5% of all trading volume'],
              ['LP Yield',         '2% to liquidity providers'],
              ['Platform Fee',     '0.5% to treasury'],
              ['Min Volume',       `$${form.minVolume} USDC before resolution`],
            ].map(([k,v])=>(
              <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                <span style={{ color:'#4a5a63' }}>{k}</span>
                <span style={{ fontWeight:600, color: k==='Your Investment'?'#00c566':'#e8eaeb' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div style={{ background:'rgba(255,68,68,.08)', border:'1px solid rgba(255,68,68,.2)', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#ff4444' }}>
            {error}
          </div>
        )}

        <button type="submit" className="btn btn-primary" disabled={loading} style={{ width:'100%', padding:'14px', fontSize:15, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
          <PlusCircle size={18} strokeWidth={2}/>
          {loading ? 'Creating Market...' : 'Create Market — Platform Seeds $10 USDC'}
        </button>
      </form>
    </div>
  );
}
